<template>
  <div :class="classes()">
    <Headline size="m" isViewHeadline>{{ $t('cs.analysis.headline') }}</Headline>

    <template v-if="!loading">
      <IntroBanner
        :headline="$t('cs.analysis.introBanner.headline')"
        :copy="$t('cs.analysis.introBanner.copy')"
        :bottomSpace="40"
        :localStorage="LS_PFLEGELEISTUNGEN_INFO_BANNER"
      />

      <template v-if="!careServiceCareLevel">
        <InViewAnimation>
          <IntroBanner
            slot="0"
            :headline="$t('cs.wizard.introBanner.error.headline')"
            :copy="$t('cs.wizard.introBanner.error.copy')"
            :bottomSpace="40"
            highlightColor="supportWarning"
            iconName="error-warning-line"
            notCloseable
          >
            <div class="u-margin__top--2">
              <Link
                :to="ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD"
                icon="arrow-right-line"
                iconPos="left"
                :onClick="
                  () =>
                    trackClick(
                      '-ohne-Pflegegrad_link-' + $t('cs.wizard.introBanner.error.link1'),
                      'link'
                    )
                "
                noUnderline
              >
                {{ $t('cs.wizard.introBanner.error.link1') }}
              </Link>
            </div>
            <div class="u-margin__top--1">
              <Link
                @click="openModal"
                icon="arrow-right-line"
                iconPos="left"
                noUnderline
                :onClick="
                  () =>
                    trackClick(
                      '-ohne-Pflegegrad_click-' + $t('cs.wizard.introBanner.error.link2'),
                      'element'
                    )
                "
              >
                {{ $t('cs.wizard.introBanner.error.link2') }}
              </Link>
            </div>
          </IntroBanner>

          <Widget slot="1" :headline="$t('cs.analysis.careLevelBar.widget.headline')">
            <CareLevelBarUI
              v-if="!!calculatedCareLevelAt"
              :headline="
                $t(
                  !!calculatedCareLevel
                    ? 'careLevelCalculator.analysis.careLevelBar.headline'
                    : 'cs.analysis.careLevelBar.headline.error'
                )
              "
              :subline="
                $t(`cs.analysis.careLevelBar.copy.error`, {
                  date: formatDate(calculatedCareLevelAt),
                })
              "
              :careLevel="calculatedCareLevel"
              :careLevelColor="!!calculatedCareLevel ? 'primaryCoral' : 'secondaryMintLight'"
              :centeredCareLevel="!!calculatedCareLevel"
            />
            <CareLevelBarUI
              v-else
              :headline="$t(`cs.analysis.careLevelBar.headline.noPG.error`)"
              :subline="$t('cs.analysis.careLevelBar.headline.noPG.copy')"
              careLevelColor="primaryCoral"
              centeredCareLevel
            />
          </Widget>
        </InViewAnimation>
      </template>

      <template v-if="careServiceCareLevel">
        <Widget
          :headline="$t('cs.analysis.cat.widget.headline')"
          :bottom="{
            href: `${ROUTE_SERVICE_PFLEGELEISTUNGSFINDER}/1`,
            label: $t(
              careServiceWizardIsFinished
                ? 'cs.analysis.cat.widget.link'
                : 'cs.analysis.cat.widget.noWizard.link'
            ),
            onClick: () =>
              trackClick(
                '-Fragebogen-Bearbeiten_' +
                  (careServiceWizardIsFinished ? 'link-' : 'btn-') +
                  $t(
                    careServiceWizardIsFinished
                      ? 'cs.analysis.cat.widget.link'
                      : 'cs.analysis.cat.widget.noWizard.link'
                  ),
                careServiceWizardIsFinished ? 'link' : 'button'
              ),
          }"
          :bottomIsButton="!careServiceWizardIsFinished"
          bottomIconName="pencil-line"
          bottomIconPos="left"
        >
          <InViewAnimation>
            <Categories slot="0" :className="el" @onTrackingClick="trackClick" />
          </InViewAnimation>
        </Widget>

        <Widget :headline="$t('cs.analysis.careLevelBar.widget.headline')">
          <InViewAnimation :delay="1">
            <CareLevelBarUI
              v-if="!!calculatedCareLevelAt && !careLevel"
              slot="0"
              :headline="
                $t(
                  !!calculatedCareLevel
                    ? 'careLevelCalculator.analysis.careLevelBar.headline'
                    : 'cs.analysis.careLevelBar.headline.error'
                )
              "
              :subline="
                $t(`cs.analysis.careLevelBar.copy.error`, {
                  date: formatDate(calculatedCareLevelAt),
                })
              "
              :careLevel="calculatedCareLevel"
              :careLevelColor="!!calculatedCareLevel ? 'primaryCoral' : 'secondaryMintLight'"
              :centeredCareLevel="!!calculatedCareLevel"
            />
            <CareLevelBar v-else slot="0" :className="el" />
          </InViewAnimation>
        </Widget>

        <Headline
          v-if="approvedCareLevelIsSelected"
          :staticClass="`${el}__subHeadine`"
          size="s"
          isSubheading
          responsive
        >
          {{ $t('cs.analysis.subHeading1') }}
        </Headline>

        <Collapsible
          v-if="careServiceCareLevel > 1"
          class="u-margin__vertical--5"
          :title="$t('cs.analysis.budgetPackage.recommendation')"
          isOpen
          likeWidget
        >
          <CareServiceBudgetRecommendation />
        </Collapsible>

        <Widget
          v-if="approvedCareLevelIsSelected"
          :headline="$t('cs.analysis.package.widget.headline')"
        >
          <div :class="`${el}__info`">
            <div :class="`${el}__infoIcon`">
              <Icon name="information-line" color="neutral0" size="l" />
            </div>
            <Copy element="div">{{ $t('cs.analysis.package.info') }}</Copy>
          </div>
          <CareServiceBudgetPackage @onTrackingClick="trackClick" />
        </Widget>

        <Headline :staticClass="`${el}__subHeadine`" size="s" isSubheading responsive>
          {{ $t('cs.analysis.subHeading2') }}
        </Headline>

        <Widget
          :headline="
            $t(
              `cs.analysis.packageSelection.widget.headline${
                currentCareLevelIsCalculated ? '.calc' : ''
              }`
            )
          "
          :bottom="
            approvedCareLevelIsSelected
              ? {
                  href: ROUTE_APPLICATIONS,
                  label: $t('cs.analysis.budgetPackage.applications.cta', {
                    value: careServiceCareLevel !== careLevel ? 0 : countDesiredValues,
                  }),
                  disabled: countDesiredValues === 0 || careServiceCareLevel !== careLevel,
                  onClick: () =>
                    trackClick(
                      '-Leistung-Beantragen_btn-' +
                        $t('cs.analysis.budgetPackage.applications.cta') +
                        '_widget',
                      'button'
                    ),
                }
              : null
          "
          bottomIsButton
        >
          <div :class="`${el}__info`">
            <div :class="`${el}__infoIcon`">
              <Icon name="information-line" color="neutral0" size="l" />
            </div>
            <Copy element="div">
              {{
                $t(
                  `cs.analysis.packageSelection.info${currentCareLevelIsCalculated ? '.calc' : ''}`
                )
              }}
            </Copy>
          </div>
          <CareServiceBudgetSelection @onTrackingClick="trackClick" />
        </Widget>

        <Widget v-if="careLevel > 1" :headline="$t('cs.analysis.carePossibility.headline')">
          <CarePossibility @onTrackingClick="trackClick" />
        </Widget>

        <template v-if="approvedCareLevelIsSelected">
          <Headline :staticClass="`${el}__subHeadine`" size="s" isSubheading responsive>
            {{ $t('cs.analysis.subHeading3') }}
          </Headline>

          <TaskSchedulerWidget @onTrackingClick="trackClick" />
        </template>
      </template>
      <Copy element="div" size="s" color="tertiaryLight" :staticClass="`${el}__legals`">
        {{ $t('cs.analysis.legalCopy') }}
      </Copy>
    </template>
    <Widget v-else>
      <Spinner size="l" insideWidget />
    </Widget>
  </div>
</template>

<script>
import Headline from '@/objects/Headline';
import Copy from '@/objects/Copy';
import Link from '@/objects/Link';
import Spinner from '@/objects/Spinner';
import Icon from '@/objects/Icon';
import Widget from '@/objects/Widget';

import InViewAnimation from '@/components/InViewAnimation';
import IntroBanner from '@/components/IntroBanner';
import Collapsible from '@/components/Collapsible';
import CareServiceBudgetPackage from '@/components/CareServiceBudgetPackage';
import CareServiceBudgetSelection from '@/components/CareServiceBudgetSelection';
import CareServiceBudgetRecommendation from '@/components/CareServiceBudgetRecommendation';
import CareLevelBarUI from '@/components/CareLevelBar';
import { TaskSchedulerWidget } from '@/views/TaskScheduler';

import {
  ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
  ROUTE_SERVICE_PFLEGELEISTUNGSFINDER,
  ROUTE_APPLICATIONS,
} from '@/constants/routes';
import { LS_PFLEGELEISTUNGEN_INFO_BANNER } from '@/constants/storage';
import { CARESERVICE_REQUEST } from '@/store/actions/careService';
import mixinCareService from '@/mixins/careService';
import mixinCareLevel from '@/mixins/careLevel';

import { HEALTH_TOGGLE_CARE_LEVEL_MODAL } from '@/store/actions/health';

import Categories from './subcomponents/Categories';
import CarePossibility from './subcomponents/CarePossibility';
import CareLevelBar from './subcomponents/CareLevelBar';

import { CARESERVICE_UPDATE_CARELEVEL, CARESERVICE_UPDATE_FACT } from '@/store/actions/careService';
import { mapFieldsCareLevelCalculator, mapFieldsHealth } from '@/store/mapFields';
import { formatDate } from '@/helpers/formatter';
import { createTrackingData } from '@/helpers/tracking/utils/helpers';

export default {
  name: 'PflegeleistungenAnalysis',
  mixins: [mixinCareService, mixinCareLevel],
  props: {},
  components: {
    Headline,
    Copy,
    Icon,
    Spinner,
    Link,
    Widget,
    IntroBanner,
    Collapsible,
    Categories,
    CarePossibility,
    CareServiceBudgetPackage,
    CareServiceBudgetSelection,
    CareServiceBudgetRecommendation,
    TaskSchedulerWidget,
    CareLevelBar,
    CareLevelBarUI,
    InViewAnimation,
  },

  computed: {
    ...mapFieldsCareLevelCalculator(['calculatedCareLevelAt', 'calculatedCareLevel']),
    ...mapFieldsHealth(['careLevelSituation']),

    approvedCareLevelIsSelected() {
      return this.careLevel && this.careServiceCareLevel === this.careLevel;
    },
  },

  data() {
    return {
      formatDate,
      loading: false,
      LS_PFLEGELEISTUNGEN_INFO_BANNER,
      ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
      ROUTE_SERVICE_PFLEGELEISTUNGSFINDER,
      ROUTE_APPLICATIONS,
      el: 'v-plfAnalysis',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },

  beforeRouteLeave(to, from, next) {
    setInterval(() => {
      next();
    }, 1);
  },

  destroyed() {
    this.$store.dispatch(CARESERVICE_UPDATE_CARELEVEL, {
      careServiceCareLevel: null,
    });
  },

  watch: {
    careLevel: function () {
      this.$store.dispatch(CARESERVICE_UPDATE_CARELEVEL, {
        careServiceCareLevel: this.careLevel,
      });
    },
  },

  created() {
    this.loading = true;
    this.$store
      .dispatch(CARESERVICE_REQUEST)
      .then(() => {
        const careLevel = this.careLevel || this.calculatedCareLevel;
        if (careLevel) {
          this.$store.dispatch(CARESERVICE_UPDATE_CARELEVEL, {
            careServiceCareLevel: this.careLevelSituation === 0 ? careLevel : null,
          });
        }

        if (!this.careLevel && this.calculatedCareLevel) {
          this.$store.dispatch(CARESERVICE_UPDATE_FACT, {
            id: 'pflegegeld',
            value: null,
          });
        }
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  },

  methods: {
    openModal() {
      this.$store.dispatch(HEALTH_TOGGLE_CARE_LEVEL_MODAL);
    },

    trackClick(labelText, element = 'element') {
      const product = 'Pflegeleistungsanalyse';
      const label = product + labelText;
      createTrackingData(product, label, element);
    },
  },
};
</script>
